import React from 'react';
import axios from 'axios';
import { Spinner } from 'reactstrap';
import { getBackendUrls } from '../../../config/backendEnv';
import { notifyError, notifySuccess } from '../../../utils/notify';

const API_BASE = process.env.NODE_ENV === 'production'
  ? 'https://oikos.ecoss.is/oikos/_inhire_api.php?action='
  : '/api/integration/';

const COLUMN_COLORS = [
  { bg: '#e3f2fd', border: '#1565c0', badge: '#1565c0' },
  { bg: '#e8f5e9', border: '#2e7d32', badge: '#2e7d32' },
  { bg: '#fffde7', border: '#f57f17', badge: '#f57f17' },
  { bg: '#fce4ec', border: '#ad1457', badge: '#ad1457' },
  { bg: '#f3e5f5', border: '#6a1b9a', badge: '#6a1b9a' },
  { bg: '#e0f7fa', border: '#00838f', badge: '#00838f' },
  { bg: '#fff3e0', border: '#ef6c00', badge: '#ef6c00' },
  { bg: '#ede7f6', border: '#4527a0', badge: '#4527a0' },
  { bg: '#f1f8e9', border: '#558b2f', badge: '#558b2f' },
];

function jobsFromListPayload(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.items)) return data.items;
  if (data && Array.isArray(data.data)) return data.data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
}

function candidatesFromPayload(data) {
  if (Array.isArray(data)) return data;
  const batch = data?.jobTalents || data?.items || data?.data || [];
  return Array.isArray(batch) ? batch : [];
}

function stageOrderValue(stage) {
  const order = Number(stage?.order);
  return Number.isFinite(order) ? order : 9999;
}

function columnFromStage(stage, index) {
  const phaseName = stage?.phase?.name || '';
  const name = stage?.name || phaseName || 'Etapa';
  return {
    key: String(stage?.id || name),
    name,
    phaseName: phaseName && phaseName !== name ? phaseName : '',
    order: stageOrderValue(stage),
    index,
  };
}

async function fetchJobStages(jobId) {
  try {
    const response = await axios.post(`${API_BASE}get-job`, { jobId });
    const job = response.data?.job || response.data;
    if (Array.isArray(job?.stages) && job.stages.length) return job.stages;
  } catch (error) {
    // A rota direta entra no ar quando o proxy reinicia. Até lá, a listagem já traz as etapas.
  }

  let lastJobId = null;
  const seen = new Set();
  for (let page = 0; page < 40; page += 1) {
    const response = await axios.post(`${API_BASE}list-jobs`, { limit: 50, lastJobId });
    const jobs = jobsFromListPayload(response.data);
    const found = jobs.find((job) => job.id === jobId);
    if (found) return Array.isArray(found.stages) ? found.stages : [];
    if (!jobs.length) break;
    const next = jobs[jobs.length - 1]?.id;
    if (!next || next === lastJobId || seen.has(next)) break;
    seen.add(next);
    lastJobId = next;
    if (jobs.length < 50) break;
  }
  return [];
}

async function fetchAllCandidates(jobId) {
  const all = [];
  let cursor = null;
  const seenCursors = new Set();
  let previousFirstId = null;

  for (let page = 0; page < 30; page += 1) {
    const response = await axios.post(`${API_BASE}list-candidates`, {
      jobId,
      ...(cursor ? { exclusiveStartKey: cursor } : {}),
    });
    const list = candidatesFromPayload(response.data);
    const firstId = list[0]?.id || list[0]?.talentId || null;
    if (page > 0 && firstId && firstId === previousFirstId) break;
    previousFirstId = firstId;
    all.push(...list);

    const next = response.data?.exclusiveStartkey || response.data?.exclusiveStartKey || null;
    const cursorKey = next ? JSON.stringify(next) : '';
    if (!next || !list.length || seenCursors.has(cursorKey)) break;
    seenCursors.add(cursorKey);
    cursor = next;
  }

  return all;
}

function buildColumns(stages, candidates) {
  const source = (Array.isArray(stages) && stages.length)
    ? stages
    : candidates.map((candidate) => candidate.stage).filter(Boolean);

  const unique = [];
  const seen = new Set();
  source
    .slice()
    .sort((a, b) => stageOrderValue(a) - stageOrderValue(b))
    .forEach((stage) => {
      const key = String(stage?.id || stage?.name || '');
      if (!key || seen.has(key)) return;
      seen.add(key);
      unique.push(columnFromStage(stage, unique.length));
    });

  const grouped = Object.fromEntries(unique.map((column) => [column.key, []]));
  const extras = [];

  candidates.forEach((candidate) => {
    const stage = candidate.stage || {};
    const byId = stage.id != null ? grouped[String(stage.id)] : null;
    const byName = unique.find((column) => column.name === stage.name);
    if (byId) {
      byId.push(candidate);
      return;
    }
    if (byName) {
      grouped[byName.key].push(candidate);
      return;
    }
    extras.push(candidate);
  });

  const columns = unique.map((column) => ({ ...column, candidates: grouped[column.key] }));
  if (extras.length) {
    columns.push({
      key: 'outros',
      name: 'Outros',
      phaseName: '',
      candidates: extras,
      index: columns.length,
    });
  }
  return columns;
}

function urlMoverEtapa() {
  const api = String(getBackendUrls().oikos || '');
  return api.replace(/_oikos_api\.php.*$/, '_inhire_mover_etapa.php');
}

function nomeDoCandidato(candidate) {
  const talent = candidate?.talent || candidate || {};
  return talent.name || 'Candidato';
}

function moverNoQuadro(columns, jobTalentId, stageId, stageName) {
  let moved = null;
  const without = columns.map((column) => {
    const remaining = [];
    column.candidates.forEach((candidate) => {
      if (!moved && candidate.id === jobTalentId) moved = candidate;
      else remaining.push(candidate);
    });
    return { ...column, candidates: remaining };
  });
  if (!moved) return columns;
  return without.map((column) => {
    if (column.key !== String(stageId)) return column;
    return {
      ...column,
      candidates: [{
        ...moved,
        stage: { ...(moved.stage || {}), id: stageId, name: stageName || column.name },
      }, ...column.candidates],
    };
  });
}

export default function InhireVagaKanban({ jobId, active = true }) {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [columns, setColumns] = React.useState([]);
  const [movingId, setMovingId] = React.useState('');
  const [dropKey, setDropKey] = React.useState('');

  React.useEffect(() => {
    if (!active || !jobId) {
      setColumns([]);
      setError('');
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError('');

    Promise.all([
      fetchJobStages(jobId),
      fetchAllCandidates(jobId),
    ]).then(([stages, candidates]) => {
      if (cancelled) return;
      const ordered = candidates.slice().sort((a, b) => {
        const dateA = new Date(a.createdAt || a.talent?.createdAt || 0);
        const dateB = new Date(b.createdAt || b.talent?.createdAt || 0);
        return dateB - dateA;
      });
      setColumns(buildColumns(stages, ordered));
    }).catch(() => {
      if (!cancelled) setError('Não foi possível carregar os candidatos desta vaga.');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [active, jobId]);

  const moverCandidato = React.useCallback(async (candidate, stageId, stageName) => {
    const jobTalentId = candidate?.id;
    if (!jobTalentId || !stageId || stageId === 'outros' || movingId) return;
    const atual = String(candidate.stage?.id || '');
    if (atual && atual === String(stageId)) return;

    setMovingId(jobTalentId);
    try {
      const response = await axios.post(urlMoverEtapa(), { jobTalentId, stageId });
      const destinoId = response.data?.stageId || stageId;
      const destinoNome = response.data?.stageName || stageName;
      setColumns((current) => moverNoQuadro(current, jobTalentId, destinoId, destinoNome));
      notifySuccess(`${nomeDoCandidato(candidate)} foi para ${destinoNome || 'a nova etapa'}. O inHire foi atualizado.`);
    } catch (requestError) {
      const detalhe = requestError?.response?.data?.erro || requestError?.response?.data?.message;
      notifyError(detalhe
        ? `Não foi possível mover o candidato. ${detalhe}`
        : 'Não foi possível mover o candidato no inHire.');
    } finally {
      setMovingId('');
      setDropKey('');
    }
  }, [movingId]);

  const aoSoltar = (event, column) => {
    event.preventDefault();
    setDropKey('');
    if (column.key === 'outros' || movingId) return;
    let payload = null;
    try {
      payload = JSON.parse(event.dataTransfer.getData('text/plain') || '');
    } catch (parseError) {
      payload = null;
    }
    if (!payload?.jobTalentId) return;
    const candidate = columns
      .flatMap((item) => item.candidates)
      .find((item) => item.id === payload.jobTalentId);
    if (!candidate) return;
    moverCandidato(candidate, column.key, column.name);
  };

  if (!jobId) return null;

  if (loading) {
    return (
      <div className="text-center p-4">
        <Spinner color="info" />
        <p className="mt-2 text-muted mb-0">Buscando etapas e candidatos no inHire...</p>
      </div>
    );
  }

  if (error) {
    return <div className="text-center p-4 text-danger">{error}</div>;
  }

  if (!columns.length) {
    return (
      <div className="text-center p-4 text-muted">
        Esta vaga não tem etapas no inHire.
      </div>
    );
  }

  return (
    <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
      <div style={{ display: 'flex', gap: 12, minWidth: columns.length * 220 }}>
        {columns.map((column) => {
          const colors = COLUMN_COLORS[column.index % COLUMN_COLORS.length];
          return (
            <div
              key={column.key}
              onDragOver={(event) => {
                if (column.key === 'outros' || movingId) return;
                event.preventDefault();
                if (dropKey !== column.key) setDropKey(column.key);
              }}
              onDragLeave={() => {
                if (dropKey === column.key) setDropKey('');
              }}
              onDrop={(event) => aoSoltar(event, column)}
              style={{
                flex: '0 0 210px',
                background: colors.bg,
                borderRadius: 8,
                border: `2px solid ${dropKey === column.key ? '#111' : colors.border}`,
                padding: 10,
              }}
            >
              <div className="d-flex justify-content-between align-items-start mb-2">
                <div>
                  <strong style={{ fontSize: '0.85rem', color: colors.border }}>{column.name}</strong>
                  {column.phaseName ? (
                    <div style={{ fontSize: '0.7rem', color: '#666' }}>{column.phaseName}</div>
                  ) : null}
                </div>
                <span
                  style={{
                    background: colors.badge,
                    color: '#fff',
                    borderRadius: '50%',
                    width: 22,
                    height: 22,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    flexShrink: 0,
                  }}
                >
                  {column.candidates.length}
                </span>
              </div>
              <div style={{ maxHeight: 400, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {column.candidates.length === 0 ? (
                  <div style={{ fontSize: '0.75rem', color: '#aaa', textAlign: 'center', paddingTop: 12 }}>
                    Nenhum candidato
                  </div>
                ) : column.candidates.map((candidate, idx) => {
                  const talent = candidate.talent || candidate;
                  const destinos = columns.filter((item) => item.key !== column.key && item.key !== 'outros');
                  const podeMover = Boolean(candidate.id) && destinos.length > 0;
                  return (
                    <div
                      key={candidate.id || talent.id || idx}
                      draggable={podeMover && !movingId}
                      onDragStart={(event) => {
                        event.dataTransfer.setData('text/plain', JSON.stringify({ jobTalentId: candidate.id }));
                        event.dataTransfer.effectAllowed = 'move';
                      }}
                      style={{
                        background: '#fff',
                        borderRadius: 6,
                        padding: '8px 10px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        fontSize: '0.8rem',
                        cursor: podeMover ? 'grab' : 'default',
                        opacity: movingId && movingId === candidate.id ? 0.55 : 1,
                      }}
                    >
                      <div style={{ fontWeight: 600, marginBottom: 2 }}>{talent.name || 'Não informado'}</div>
                      <div style={{ color: '#666', wordBreak: 'break-all' }}>{talent.email || ''}</div>
                      {(talent.phone || talent.cellphone) && (
                        <div style={{ color: '#888' }}>{talent.phone || talent.cellphone}</div>
                      )}
                      <div style={{ color: '#aaa', marginTop: 4, fontSize: '0.72rem' }}>
                        {candidate.createdAt
                          ? new Date(candidate.createdAt).toLocaleDateString('pt-BR')
                          : '—'}
                      </div>
                      {podeMover ? (
                        <select
                          aria-label={`Mover ${talent.name || 'candidato'}`}
                          value=""
                          disabled={Boolean(movingId)}
                          onChange={(event) => {
                            const destino = destinos.find((item) => item.key === event.target.value);
                            if (destino) moverCandidato(candidate, destino.key, destino.name);
                          }}
                          style={{ marginTop: 6, width: '100%', fontSize: '0.72rem' }}
                        >
                          <option value="">Mover para...</option>
                          {destinos.map((destino) => (
                            <option key={destino.key} value={destino.key}>{destino.name}</option>
                          ))}
                        </select>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
